import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Send, ShieldCheck, CheckCircle2, Clock, MessageCircle } from 'lucide-react';
import { employmentStatusOptions, companyInfo } from '../mockData';
import { toast } from 'sonner';
import { getFbTrackingParams } from '../utils/fbTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formBadges = [
  "Maklumat Sulit",
  "Semakan Percuma",
  "Tiada Komitmen",
  "Khas Penjawat Awam Sarawak"
];

const QualificationForm = React.forwardRef((props, ref) => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    employmentStatus: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const buildWhatsAppUrl = () => {
    const message = encodeURIComponent(
      'Assalamualaikum, saya telah membuat semakan awal melalui website dan ingin mendapatkan maklumat lanjut.'
    );
    return `https://wa.me/${companyInfo.whatsappNumber}?text=${message}`;
  };

  const redirectToWhatsApp = (url) => {
    // Best for Facebook in-app browser, Safari and Chrome
    // Try window.location.href which works in in-app browsers
    try {
      window.location.href = url;
    } catch (e) {
      // Fallback to window.open
      window.open(url, '_blank');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.phone || !formData.employmentStatus) {
      toast.error('Sila lengkapkan semua maklumat');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get Facebook tracking params (fbc, fbp)
      const fbParams = getFbTrackingParams();

      const response = await axios.post(`${API}/lead-submissions`, {
        fullName: formData.fullName,
        phone: formData.phone,
        employer: 'N/A',
        employmentStatus: formData.employmentStatus,
        grossSalary: '0',
        currentDeduction: '0',
        ctosStatus: 'N/A',
        fbc: fbParams.fbc,
        fbp: fbParams.fbp,
        eventSourceUrl: window.location.href
      });

      if (response.data.success) {
        // Facebook Pixel - Track Lead event (client-side) with deduplication
        if (typeof window.fbq !== 'undefined') {
          window.fbq('track', 'Lead', {
            content_name: 'Semakan Kelayakan Awal',
            content_category: formData.employmentStatus,
            value: 1.00,
            currency: 'MYR'
          }, {
            eventID: response.data.data.id  // Match with server-side event_id
          });
        }

        const url = buildWhatsAppUrl();
        setWhatsappUrl(url);
        setShowSuccess(true);
        
        // Auto-redirect after 2 seconds
        setTimeout(() => {
          redirectToWhatsApp(url);
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error.response?.data?.message || 'Ralat menghantar maklumat. Sila cuba lagi.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualWhatsApp = () => {
    redirectToWhatsApp(whatsappUrl || buildWhatsAppUrl());
  };

  // Success State
  if (showSuccess) {
    return (
      <section ref={ref} className="py-12 sm:py-16 bg-gradient-to-b from-emerald-50 to-white" id="form-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <Card className="shadow-2xl border-t-4 border-green-500">
              <CardContent className="p-8 sm:p-12 text-center">
                {/* Success Icon */}
                <div className="flex justify-center mb-6">
                  <div className="bg-green-100 p-5 rounded-full animate-pulse">
                    <CheckCircle2 className="h-16 w-16 sm:h-20 sm:w-20 text-green-600" />
                  </div>
                </div>

                {/* Success Message */}
                <h2 className="text-2xl sm:text-3xl font-bold text-emerald-900 mb-3" data-testid="success-message">
                  Terima kasih!
                </h2>
                <p className="text-base sm:text-lg text-emerald-700 mb-2">
                  Semakan awal anda telah diterima.
                </p>
                <p className="text-sm sm:text-base text-emerald-600 mb-8">
                  Mengalihkan anda ke WhatsApp dalam beberapa saat...
                </p>

                {/* Loading Indicator */}
                <div className="flex justify-center mb-8">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                    <div className="w-3 h-3 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                    <div className="w-3 h-3 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                  </div>
                </div>

                {/* Fallback Button */}
                <div className="border-t border-emerald-100 pt-6">
                  <p className="text-sm text-emerald-600 mb-4">
                    Tidak teralih secara automatik? Klik butang di bawah:
                  </p>
                  <Button
                    size="lg"
                    onClick={handleManualWhatsApp}
                    data-testid="success-whatsapp-fallback"
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-bold text-base sm:text-lg py-6 sm:py-7 rounded-lg shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 border-2 border-green-400 animate-pulse-cta"
                  >
                    <MessageCircle className="mr-2 h-5 w-5 sm:h-6 sm:w-6" />
                    WHATSAPP PEGAWAI SEKARANG
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} className="py-12 sm:py-16 bg-gradient-to-b from-emerald-50 to-white" id="form-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Trust Badges Above Form */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-5">
            {formBadges.map((badge, index) => (
              <div
                key={index}
                className="flex items-center gap-1.5 bg-emerald-100 px-3 py-1.5 rounded-full border border-emerald-200"
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700 flex-shrink-0" />
                <span className="text-emerald-900 text-xs sm:text-sm font-semibold">{badge}</span>
              </div>
            ))}
          </div>

          {/* Time Banner */}
          <div className="flex items-center justify-center gap-2 bg-amber-50 border border-amber-300 rounded-lg px-4 py-2.5 mb-5">
            <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-amber-700 flex-shrink-0" />
            <p className="text-amber-900 text-xs sm:text-sm font-semibold text-center">
              Semakan awal mengambil masa kurang daripada 2 minit.
            </p>
          </div>

          {/* Form Card */}
          <Card className="shadow-2xl border-t-4 border-amber-500">
            <CardHeader className="text-center bg-gradient-to-br from-emerald-900 to-emerald-800 text-white rounded-t-lg py-6 sm:py-8">
              <div className="flex justify-center mb-3">
                <div className="bg-amber-500 p-3 rounded-full">
                  <ShieldCheck className="h-8 w-8 text-emerald-950" />
                </div>
              </div>
              <CardTitle className="text-2xl sm:text-3xl font-bold mb-2">Semakan Kelayakan Awal</CardTitle>
              <CardDescription className="text-emerald-50 text-base sm:text-lg">
                Hanya 3 maklumat. Percuma & Pantas!
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-emerald-900 font-semibold text-base">Nama Penuh *</Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Masukkan nama penuh"
                    className="border-emerald-200 focus:border-amber-500 focus:ring-amber-500 h-12 text-base"
                    required
                    disabled={isSubmitting}
                    data-testid="form-fullname-input"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-emerald-900 font-semibold text-base">No Telefon *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="01X-XXX XXXX"
                    className="border-emerald-200 focus:border-amber-500 focus:ring-amber-500 h-12 text-base"
                    required
                    disabled={isSubmitting}
                    data-testid="form-phone-input"
                  />
                </div>

                {/* Employment Status */}
                <div className="space-y-2">
                  <Label htmlFor="employmentStatus" className="text-emerald-900 font-semibold text-base">Status Pekerjaan *</Label>
                  <Select 
                    onValueChange={(value) => handleSelectChange('employmentStatus', value)}
                    disabled={isSubmitting}
                    value={formData.employmentStatus}
                  >
                    <SelectTrigger 
                      className="border-emerald-200 focus:border-amber-500 focus:ring-amber-500 h-12 text-base"
                      data-testid="form-employment-trigger"
                    >
                      <SelectValue placeholder="Pilih status pekerjaan" />
                    </SelectTrigger>
                    <SelectContent>
                      {employmentStatusOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  data-testid="form-submit-button"
                  className="w-full bg-amber-500 hover:bg-amber-600 text-emerald-950 font-bold text-base sm:text-lg py-6 sm:py-7 rounded-lg shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed animate-pulse-cta"
                >
                  {isSubmitting ? 'MENGHANTAR...' : 'SEMAK KELAYAKAN SAYA'}
                  {!isSubmitting && <Send className="ml-2 h-5 w-5" />}
                </Button>

                <p className="text-sm text-center text-emerald-600 pt-2">
                  🔒 Semua maklumat adalah sulit dan selamat
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
});

QualificationForm.displayName = 'QualificationForm';

export default QualificationForm;
